import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WettkampferAuswahlModal from '../../components/registration/WettkampferAuswahlModal';
import CsvAnmeldungModal from '../../components/registration/CsvAnmeldungModal';
import './VereinsDashboard.css';
import ZahlungKasten from './ZahlungKasten';
import Mitteilungen from './Mitteilungen';
import AnmeldeBestaetigung from './AnmeldeBestaetigung';
import AnmeldeAblauf from '../../components/anmeldung/AnmeldeAblauf';
import { skillAusGurt } from '../../lib/skillAusGurt';
import { deutscherName } from '../../lib/klassenEinfach';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '–';

/**
 * Betrag als Euro, oder ein Strich.
 *
 * ⚠️ `null` heisst „keine Gebuehr hinterlegt" und wird als „–" gezeigt, NICHT
 * als 0,00 €. Der Unterschied ist wichtig: eine Vormerkung ohne Kategorie hat
 * noch keinen Start und damit keine Gebuehr; 0,00 € waere die Aussage „kostet
 * nichts".
 */
const euro = (betrag) => {
  const n = parseFloat(betrag);
  return Number.isFinite(n)
    ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
    : '–';
};

/** Summe der Starts, die nicht storniert sind. */
const summeOffen = (zeilen = []) =>
  zeilen
    .filter((r) => r.status !== 'Storniert')
    .reduce((s, r) => s + (parseFloat(r.startgebuehr) || 0), 0);

const GURTFARBEN = [
  'Weiß', 'Weiß-Gelb', 'Gelb', 'Gelb-Grün', 'Orange', 'Grün',
  'Grün-Blau', 'Blau', 'Blau-Rot', 'Rot', 'Rot-Braun', 'Braun', 'Schwarz',
];

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Black Belt'];

// „Skill Level“ heißt auf Deutsch Erfahrung (25.10.2026, Wörterliste im Muster
// Vereinsanmeldung). Gespeichert wird weiter der englische Wert (ENUM in
// wettkaempfer.skill_level, Divisionen filtern danach), gezeigt der deutsche.
const ERFAHRUNG = {
  'Beginner': 'Anfänger',
  'Intermediate': 'Fortgeschritten',
  'Advanced': 'Erfahren',
  'Black Belt': 'Schwarzgurt',
};
const erfahrung = (s) => ERFAHRUNG[s] || s;

// Pflichtangaben wie der Server (middleware/validation.js validateWettkaempfer),
// damit der Fehler am Feld steht, bevor gespeichert wird.
function wkPruefen(f) {
  const e = {};
  if (!f.vorname || f.vorname.trim().length < 2) e.vorname = 'Bitte den Vornamen eintragen (mindestens 2 Zeichen)';
  if (!f.nachname || f.nachname.trim().length < 2) e.nachname = 'Bitte den Nachnamen eintragen (mindestens 2 Zeichen)';
  if (!f.geburtsdatum) e.geburtsdatum = 'Bitte das Geburtsdatum eintragen';
  if (!f.geschlecht) e.geschlecht = 'Bitte auswählen, wir geben hier bewusst nichts vor';
  const g = parseFloat(String(f.gewicht ?? '').replace(',', '.'));
  if (!(g > 0)) e.gewicht = 'Bitte das Gewicht eintragen';
  else if (g < 15 || g > 250) e.gewicht = 'Das Gewicht muss zwischen 15 und 250 kg liegen';
  return e;
}

/** Hat der Verein im Formular schon etwas eingetragen oder geändert? */
const wkGeaendert = (form, start) => Object.keys({ ...form, ...start })
  .some((k) => String(form[k] ?? '') !== String(start[k] ?? ''));

// Gurt → Skill-Level: gemeinsame Regel (Aufgabe 161), Verein darf danach abweichen
const gurtToSkill = (gurt) => skillAusGurt(gurt) || 'Beginner';

const SKILL_BADGE = {
  'Beginner':    'muted',
  'Intermediate':'blue',
  'Advanced':    'gold',
  'Black Belt':  'black',
};

export default function VereinsDashboard() {
  const navigate = useNavigate();
  // Ein Anmeldeweg für Vereine (V17, 18.10.2026): /verein?turnier=<id> öffnet
  // die Anmeldung zu diesem Turnier direkt (&art=datei die Sammelanmeldung per
  // CSV). Dorthin leiten Turnierseite, Login und die alte Adresse /registration.
  const [suchParameter, setSuchParameter] = useSearchParams();

  // Auth
  const stored = JSON.parse(localStorage.getItem('clubData') || '{}');
  const vereinsId = stored?.user?.vereins_id;
  const vereinName = stored?.user?.name || 'Mein Verein';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [tab, setTab] = useState('übersicht');
  const [profile, setProfile] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [turniere, setTurniere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Profil-Bearbeitung
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  // Wettkämpfer-Modal
  const [wkModal, setWkModal] = useState(null); // null | { type: 'add'|'edit', start }
  const [wkForm, setWkForm] = useState({});
  const [wkFehler, setWkFehler] = useState({});      // { feld: Meldung } am Feld (25.10.2026)
  const [wkMeldung, setWkMeldung] = useState('');    // Meldung IM Fenster, nicht dahinter
  const [wkSpeichert, setWkSpeichert] = useState(false);

  // Turnier, für das angemeldet werden soll (?turnier=), solange noch Sportler
  // fehlen. Bis 25.10.2026 kam nur eine Meldung, die nach Sekunden verschwand,
  // und der Turnierbezug war weg (Befund 11_verein_ohne_wk).
  const [turnierKontext, setTurnierKontext] = useState(null);

  // Bestätigung nach dem Absenden (25.10.2026): was angemeldet ist, Startgeld,
  // wie es weitergeht. Vorher nur ein grüner Streifen, der verschwand.
  const [bestaetigung, setBestaetigung] = useState(null); // { turnier, wkIds, info }

  // Turnier-Anmeldungs-Modal
  const [turnierModal, setTurnierModal] = useState(null); // null | { turnier } — nur Vormerken (ohne Datum)
  const [anmeldeTurnier, setAnmeldeTurnier] = useState(null); // Kategorien-Anmeldung (WettkampferAuswahlModal)
  const [csvTurnier, setCsvTurnier] = useState(null); // Sammelanmeldung per Datei (CsvAnmeldungModal)
  // Geführte Anmeldung in vier Schritten (25.10.2026, components/anmeldung/AnmeldeAblauf)
  const [ablauf, setAblauf] = useState(null); // null | turnier

  // Hell/Dunkel wie im Dashboard (localStorage tda-theme, App.js setzt es beim Start)
  const [thema, setThema] = useState(() => { try { return localStorage.getItem('tda-theme') || document.documentElement.getAttribute('data-theme') || 'light'; } catch { return 'light'; } });
  const themaWechseln = () => {
    const neu = thema === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', neu);
    try { localStorage.setItem('tda-theme', neu); } catch { /* ohne Speicher nur für diese Sitzung */ }
    setThema(neu);
  };
  const [selectedAthletes, setSelectedAthletes] = useState(new Set());

  // Übersicht Sub-Tab
  const [overviewTab, setOverviewTab] = useState('dashboard');

  // Suche
  const [wkSearch, setWkSearch] = useState('');

  // Dojosoftware-Integration
  const [integration, setIntegration] = useState(null);  // null = nicht geladen
  const [integForm, setIntegForm] = useState({ dojo_api_url: '', api_token: '' });
  const [integLoading, setIntegLoading] = useState(false);
  const [integTestResult, setIntegTestResult] = useState(null); // null | { ok, msg }

  // Auth-Check
  useEffect(() => {
    if (!vereinsId) {
      navigate('/anmelden', { replace: true });
      return;
    }
    loadAll();
    loadIntegration();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3500);
  };

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [profRes, athRes, anmRes, turnRes] = await Promise.all([
        fetch(`/api/vereine/${vereinsId}`, { headers }),
        fetch(`/api/wettkaempfer/verein/${vereinsId}`, { headers }),
        fetch(`/api/anmeldungen/verein/${vereinsId}`, { headers }),
        fetch('/api/turniere/public'),
      ]);

      const [pData, aData, anmData, tData] = await Promise.all([
        profRes.json(), athRes.json(), anmRes.json(), turnRes.json(),
      ]);

      // Profil
      const p = pData.data || pData;
      if (p && (p.vereins_id || p.name)) {
        setProfile(p);
        setProfileForm({
          name: p.name || '', email: p.email || '',
          ansprechpartner: p.ansprechpartner || '', telefon: p.telefon || '',
          homepage: p.homepage || '',
          strasse: p.strasse || '', hausnummer: p.hausnummer || '',
          plz: p.plz || '', ort: p.ort || '',
        });
      }

      // Wettkämpfer
      const aList = aData.success ? (aData.data || []) : (Array.isArray(aData) ? aData : []);
      setAthletes(aList);

      // Anmeldungen — API returns { success, data: { anmeldungen: [...] } }
      const rawAnm = anmData?.data?.anmeldungen ?? anmData?.data ?? anmData;
      setAnmeldungen(Array.isArray(rawAnm) ? rawAnm : []);

      // Turniere (public)
      const tList = tData.success
        ? (tData.data || [])
        : (Array.isArray(tData) ? tData : []);
      setTurniere(tList);

    } catch (e) {
      console.error('Ladefehler:', e);
      showMsg('Fehler beim Laden der Daten.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Dojosoftware-Integration laden
  const loadIntegration = async () => {
    try {
      const r = await fetch(`/api/dojo-sync/integration/${vereinsId}`, { headers });
      const d = await r.json();
      if (d.success) {
        setIntegration(d.integration);
        if (d.integration) {
          setIntegForm({
            dojo_api_url: d.integration.dojo_api_url || '',
            api_token: '',
          });
        }
      }
    } catch { /* ignorieren */ }
  };

  const testConnection = async () => {
    if (!integForm.dojo_api_url || !integForm.api_token) {
      setIntegTestResult({ ok: false, msg: 'Bitte API-URL und API-Token eingeben.' });
      return;
    }
    setIntegLoading(true);
    setIntegTestResult(null);
    try {
      const r = await fetch('/api/dojo-sync/check-connection', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dojo_api_url: integForm.dojo_api_url, api_token: integForm.api_token }),
      });
      const d = await r.json();
      if (d.success) {
        const dojo = d.dojos?.[0] || d.authenticated_dojo;
        setIntegTestResult({ ok: true, msg: `Verbindung erfolgreich! Dojo: ${dojo?.name || '–'} (${dojo?.mitglieder_count ?? '?'} Mitglieder)` });
      } else {
        setIntegTestResult({ ok: false, msg: d.message || 'Verbindung fehlgeschlagen.' });
      }
    } catch (e) {
      setIntegTestResult({ ok: false, msg: 'Verbindungsfehler: ' + e.message });
    } finally {
      setIntegLoading(false);
    }
  };

  const saveIntegration = async () => {
    if (!integForm.dojo_api_url || !integForm.api_token) {
      showMsg('Bitte API-URL und API-Token eingeben.', 'error');
      return;
    }
    setIntegLoading(true);
    try {
      const r = await fetch(`/api/dojo-sync/integration/${vereinsId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(integForm),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        await loadIntegration();
        setIntegForm(f => ({ ...f, api_token: '' }));
        showMsg('Integration gespeichert.');
      } else {
        showMsg(d.message || 'Fehler beim Speichern.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
    finally { setIntegLoading(false); }
  };

  const syncNow = async () => {
    setIntegLoading(true);
    try {
      const r = await fetch('/api/dojo-sync/sync-verein', {
        method: 'POST',
        headers,
      });
      const d = await r.json();
      if (d.success) {
        await loadAll(true);
        await loadIntegration();
        showMsg(`Sync abgeschlossen: ${d.imported} neu, ${d.updated} aktualisiert${d.errors ? `, ${d.errors} Fehler` : ''}.`);
      } else {
        showMsg(d.message || 'Sync fehlgeschlagen.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
    finally { setIntegLoading(false); }
  };

  // Logout
  const handleLogout = () => {
    ['token', 'authToken', 'clubData', 'clubId', 'adminData', 'vereins_id']
      .forEach(k => localStorage.removeItem(k));
    navigate('/anmelden', { replace: true });
  };

  // Profil speichern
  const saveProfile = async () => {
    try {
      const r = await fetch(`/api/vereine/${vereinsId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        setProfile({ ...profile, ...profileForm });
        setEditProfile(false);
        showMsg('Profil gespeichert.');
      } else {
        showMsg(d.error || d.message || 'Fehler beim Speichern.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Fenster „Sportler anlegen/bearbeiten“ öffnen. Geschlecht ohne Vorbelegung
  // (25.10.2026): vorher stand „männlich“ drin, ein Mädchen wurde ohne Absicht
  // als Junge gespeichert. Gurt Weiß und Erfahrung daraus bleiben Vorschlag.
  const wkOeffnen = (typ, daten) => {
    const start = typ === 'add'
      ? { geschlecht: '', gurtfarbe: 'Weiß', skill_level: gurtToSkill('Weiß') }
      : { ...daten };
    setWkForm(start);
    setWkFehler({});
    setWkMeldung('');
    setWkModal({ type: typ, start });
  };

  // Schließen nur über Knopf; wurde schon etwas eingetragen, erst fragen
  const wkSchliessen = () => {
    if (wkModal && wkGeaendert(wkForm, wkModal.start)
      && !window.confirm('Deine Eingaben gehen verloren. Fenster trotzdem schließen?')) return;
    setWkModal(null);
  };

  const wkSetze = (feld, wert, extra = {}) => {
    setWkForm((f) => ({ ...f, [feld]: wert, ...extra }));
    if (wkFehler[feld]) setWkFehler((x) => { const n = { ...x }; delete n[feld]; return n; });
  };

  const wkFehlerZeigen = (fehler) => {
    setWkFehler(fehler);
    const erstes = ['vorname', 'nachname', 'geburtsdatum', 'geschlecht', 'gewicht', 'gurtfarbe', 'skill_level', 'email', 'foto_galerie_vorgabe']
      .find((k) => fehler[k]) || Object.keys(fehler)[0];
    setTimeout(() => {
      const el = document.getElementById(`wk_${erstes}`);
      if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.focus({ preventScroll: true }); }
    }, 0);
  };

  // Wettkämpfer speichern (add/edit)
  const saveWk = async () => {
    const isEdit = wkModal?.type === 'edit';
    const id = wkForm.wettkaempfer_id || wkForm.id;
    const url = isEdit ? `/api/wettkaempfer/${id}` : '/api/wettkaempfer';
    setWkMeldung('');
    const vorher = wkPruefen(wkForm);
    if (Object.keys(vorher).length) { wkFehlerZeigen(vorher); return; }
    setWkSpeichert(true);
    try {
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wkForm, gewicht: String(wkForm.gewicht ?? '').replace(',', '.'), vereins_id: vereinsId }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success !== false) {
        setWkModal(null);
        await loadAll(true);
        showMsg(isEdit ? 'Sportler gespeichert.' : `${wkForm.vorname} ${wkForm.nachname} ist angelegt.`);
      } else if (d.felder && Object.keys(d.felder).length) {
        // Server nennt das Feld: dort zeigen (vorher oben hinter dem Schleier)
        wkFehlerZeigen(d.felder);
      } else {
        const grund = Array.isArray(d.details) && d.details.length > 0
          ? d.details.join(' · ')
          : (d.error || d.message || `Nicht gespeichert (Fehler ${r.status}).`);
        setWkMeldung(grund);
      }
    } catch { setWkMeldung('Keine Verbindung zum Server. Bitte erneut versuchen.'); }
    finally { setWkSpeichert(false); }
  };

  // Wettkämpfer löschen
  const deleteWk = async (wk) => {
    if (!window.confirm(`${wk.vorname} ${wk.nachname} wirklich löschen?`)) return;
    const id = wk.wettkaempfer_id || wk.id;
    try {
      const r = await fetch(`/api/wettkaempfer/${id}`, { method: 'DELETE', headers });
      if (r.ok) {
        await loadAll(true);
        showMsg('Wettkämpfer gelöscht.');
      } else {
        const d = await r.json().catch(() => ({}));
        showMsg(d.error || 'Fehler beim Löschen.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Turnier-Anmeldungs-Modal öffnen
  const openTurnierModal = (turnier) => {
    const tid = turnier.turnier_id || turnier.id;
    const already = new Set(
      anmeldungen
        .filter(a => a.turnier_id === tid && a.status !== 'Storniert')
        .map(a => a.wettkampfer_id || a.wettkaempfer_id)
    );
    setSelectedAthletes(already);
    setTurnierModal({ turnier });
  };

  // Ein Weg für Vereine (27.09.2026): „Anmelden" öffnet direkt das Fenster
  // Wettkämpfer → Kategorien → fertig. Vorher meldete der Knopf nur OHNE
  // Kategorie an („Vormerken"), und für die Kategorien ging es auf eine
  // zweite Seite mit derselben Turnierliste noch einmal. Vormerkungen werden
  // beim Speichern durch die Kategorien ersetzt (anmeldungenController).
  // Ohne Datum bleibt es beim Vormerken (Entscheidung 16.09.2026).
  //
  // Seit 25.10.2026 führt „Anmelden“ durch die geführte Anmeldung
  // (AnmeldeAblauf: Checkliste, 1 Sportler, 2 Klassen, 3 Prüfen, 4 Fertig).
  // Das bisherige Fenster bleibt für Turniere im Smart-Modus (Disziplinen statt
  // Klassen) und als „Ausführliche Ansicht“ aus Schritt 2.
  const oeffneAnmeldung = (turnier) => {
    const tid = turnier.turnier_id || turnier.id;
    if (!turnier.datum) { openTurnierModal(turnier); return; }
    if (turnier.modus === 'smart') {
      if (athletes.length === 0) { setTurnierKontext({ ...turnier, turnier_id: tid }); setTab('wettkämpfer'); wkOeffnen('add'); return; }
      setAnmeldeTurnier({ ...turnier, turnier_id: tid });
      return;
    }
    setBestaetigung(null);
    setTurnierKontext(null);
    setAblauf({ ...turnier, turnier_id: tid });
    window.scrollTo({ top: 0 });
  };

  // ?turnier=<id> einmal auswerten, sobald die Turniere geladen sind
  const [direktGeprueft, setDirektGeprueft] = useState(false);
  useEffect(() => {
    const wunsch = suchParameter.get('turnier');
    if (!wunsch || loading || direktGeprueft) return;
    setDirektGeprueft(true);
    const t = turniere.find(x => String(x.turnier_id || x.id) === String(wunsch));
    const rest = new URLSearchParams(suchParameter);
    rest.delete('turnier'); rest.delete('art');
    setSuchParameter(rest, { replace: true });
    setTab('veranstaltungen');
    if (!t) { showMsg('Für dieses Turnier ist gerade keine Anmeldung möglich.', 'error'); return; }
    if (athletes.length === 0 && (!t.datum || t.modus === 'smart')) {
      // Turnier merken und zum Anlegen führen; danach „Weiter zur Anmeldung“.
      // Die geführte Anmeldung (mit Datum) legt Sportler selbst in Schritt 1 an.
      setTurnierKontext({ ...t, turnier_id: t.turnier_id || t.id });
      setTab('wettkämpfer');
      wkOeffnen('add');
      return;
    }
    if (suchParameter.get('art') === 'datei' && t.datum) setCsvTurnier({ ...t, turnier_id: t.turnier_id || t.id });
    else oeffneAnmeldung(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- läuft einmal nach dem Laden; oeffneAnmeldung/showMsg sind stabil genug
  }, [loading, turniere, suchParameter, direktGeprueft]);

  const anmeldungGespeichert = async (daten) => {
    const t = daten?.turnier;
    const tid = t?.turnier_id || t?.id;
    await loadAll(true);
    setTurnierKontext(null);
    // Ausschreibungsangaben (Zahlungsfrist, Abmeldung, Kontakt) für „Wie geht es weiter“
    let info = null;
    try {
      const r = await fetch(`/api/turniere/public/${tid}/info`);
      const x = await r.json().catch(() => null);
      if (r.ok && x?.data) info = x.data;
    } catch { /* ohne Zusatzangaben weiter */ }
    setBestaetigung({ turnier: t, wkIds: (daten?.wettkampfer || []).map(Number), info });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleAthlete = (id) => {
    setSelectedAthletes(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submitTurnierAnmeldung = async () => {
    if (!turnierModal || selectedAthletes.size === 0) return;
    const turnierId = turnierModal.turnier.turnier_id || turnierModal.turnier.id;
    try {
      const r = await fetch(`/api/turniere/${turnierId}/register`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ wettkampfer_ids: [...selectedAthletes] }),
      });
      const d = await r.json();
      if (d.success || r.ok) {
        setTurnierModal(null);
        await loadAll(true);
        showMsg(`${selectedAthletes.size} Wettkämpfer vorgemerkt — Kategorien fehlen noch.`);
      } else {
        showMsg(d.error || d.message || 'Fehler bei der Anmeldung.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Anmeldung stornieren
  //
  // ⚠️ Der Server verlangt einen Stornierungsgrund (mindestens 3 Zeichen) --
  // hier ging der DELETE bis zum 16.09.2026 OHNE RUMPF raus. Er antwortete
  // deshalb immer mit 400, und Stornieren war ueber das Vereins-Dashboard
  // ueberhaupt nicht moeglich. Der Grund wird jetzt erfragt und mitgeschickt.
  const storniereAnmeldung = async (anm) => {
    const turnierId = anm.turnier_id;
    const anmId = anm.anmeldung_id || anm.id;
    const wer = `${anm.vorname} ${anm.nachname}`;
    const kategorie = anm.division_code ? ` (${anm.division_code})` : '';
    const grund = window.prompt(
      `Anmeldung von ${wer}${kategorie} stornieren.\n\nWarum? Der Grund wird beim Start vermerkt.`,
      ''
    );
    // Abbrechen im Fenster liefert null -- dann passiert nichts.
    if (grund === null) return;
    if (grund.trim().length < 3) {
      showMsg('Bitte einen Grund mit mindestens 3 Zeichen angeben.', 'error');
      return;
    }
    try {
      const r = await fetch(`/api/turniere/${turnierId}/registrations/${anmId}`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ grund: grund.trim() }),
      });
      if (r.ok) {
        await loadAll(true);
        showMsg('Anmeldung storniert.');
      } else {
        const d = await r.json().catch(() => ({}));
        showMsg(d.error || 'Fehler beim Stornieren.', 'error');
      }
    } catch { showMsg('Verbindungsfehler.', 'error'); }
  };

  // Computed
  const filteredAthletes = athletes.filter(a =>
    `${a.vorname} ${a.nachname}`.toLowerCase().includes(wkSearch.toLowerCase())
  );

  const safeAnmeldungen = Array.isArray(anmeldungen) ? anmeldungen : [];
  const byTurnier = safeAnmeldungen.reduce((map, a) => {
    const k = a.turnier_id;
    if (!map[k]) map[k] = { id: k, name: a.turnier_name, datum: a.turnier_datum, rows: [] };
    map[k].rows.push(a);
    return map;
  }, {});
  const turnierGroups = Object.values(byTurnier).sort((a, b) => new Date(b.datum) - new Date(a.datum));

  const activeStarts = safeAnmeldungen.filter(a => a.status === 'Angemeldet' || a.status === 'Bestätigt').length;

  const statusBadge = (s) => {
    if (s === 'Storniert') return 'danger';
    if (s === 'Bestätigt') return 'success';
    return 'warning';
  };

  // ── Loading State ──────────────────────────────────────────────────────────
  if (loading) return (
    <div className="vd-loading">
      <div className="vd-loading-spinner" />
      <p>Lade Vereinsdashboard…</p>
    </div>
  );

  return (
    <div className="vd-page">

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <nav className="vd-topbar">
        <div className="vd-topbar-left">
          <img src="/logo.webp" alt="TDA" className="vd-topbar-logo" />
          <div className="vd-topbar-namen">
            <span className="vd-topbar-title">TDA Tournament Center</span>
            <span className="vd-topbar-sub"><span className="vd-topbar-strich"> — </span>{profile?.name || vereinName}</span>
          </div>
        </div>
        <div className="vd-topbar-right">
          <span className="vd-topbar-badge">Verein</span>
          <button type="button" className="vd-btn vd-btn--ghost vd-btn--sm vd-thema" onClick={themaWechseln}
            aria-label={thema === 'dark' ? 'Helle Ansicht' : 'Dunkle Ansicht'} title={thema === 'dark' ? 'Helle Ansicht' : 'Dunkle Ansicht'}>
            {thema === 'dark' ? '☀' : '☾'}
          </button>
          <button className="vd-btn vd-btn--ghost vd-btn--sm" onClick={handleLogout}>
            Abmelden
          </button>
        </div>
      </nav>

      {/* ── Message ────────────────────────────────────────────────────────── */}
      {message.text && (
        <div className={`vd-message vd-message--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      {!ablauf && <div className="vd-tabs-bar">
        {[
          ['übersicht',      '📊 Übersicht'],
          ['wettkämpfer',    '🥋 Wettkämpfer'],
          ['veranstaltungen','🏆 Veranstaltungen'],
          ['anmeldungen',    '📋 Anmeldungen'],
          ['profil',         '⚙️ Profil'],
          ['integration',    '🔗 Dojosoftware'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`vd-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>}

      <div className="vd-content">
        {ablauf && (
          <AnmeldeAblauf
            key={ablauf.turnier_id}
            turnier={ablauf}
            athletes={athletes}
            anmeldungen={safeAnmeldungen}
            vereinsId={vereinsId}
            headers={headers}
            onSportlerAnlegen={() => wkOeffnen('add')}
            onSportlerBearbeiten={(a) => wkOeffnen('edit', a)}
            onDatei={() => setCsvTurnier(ablauf)}
            onGespeichert={() => loadAll(true)}
            onSchliessen={(ziel) => { setAblauf(null); setTab(ziel || 'veranstaltungen'); window.scrollTo({ top: 0 }); }}
            onAusfuehrlich={() => { const t = ablauf; setAblauf(null); setAnmeldeTurnier(t); }}
          />
        )}
        {turnierKontext && !bestaetigung && !ablauf && (
          <div className="vd-kontext" role="status">
            <div>
              <strong>Anmeldung für {turnierKontext.name}</strong>
              <div>{athletes.length === 0
                ? 'Lege zuerst deine Sportler an. Danach geht es hier direkt weiter zur Anmeldung.'
                : `${athletes.length} ${athletes.length === 1 ? 'Sportler ist' : 'Sportler sind'} angelegt. Weitere anlegen oder jetzt anmelden.`}</div>
            </div>
            <div className="vd-kontext-knoepfe">
              {athletes.length === 0
                ? <button className="vd-btn vd-btn--haupt" onClick={() => wkOeffnen('add')}>Sportler anlegen</button>
                : <button className="vd-btn vd-btn--haupt" onClick={() => { const t = turnierKontext; setTurnierKontext(null); oeffneAnmeldung(t); }}>Weiter zur Anmeldung</button>}
              <button className="vd-btn vd-btn--ghost" onClick={() => setTurnierKontext(null)}>Später</button>
            </div>
          </div>
        )}

        {bestaetigung && !ablauf && (
          <AnmeldeBestaetigung
            daten={bestaetigung}
            anmeldungen={safeAnmeldungen}
            vereinsId={vereinsId}
            headers={headers}
            onWeitere={() => { const t = bestaetigung.turnier; setBestaetigung(null); oeffneAnmeldung(t); }}
            onUebersicht={() => { setBestaetigung(null); setTab('anmeldungen'); }}
          />
        )}

        {!bestaetigung && !ablauf && <>
        <Mitteilungen token={token} />

        {/* ══════════════════════════════════════════════════════════════════
            ÜBERSICHT
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'übersicht' && (
          <div>
            {/* ── Sub-Tabs ── */}
            <div className="vd-sub-tabs">
              <button
                className={`vd-sub-tab ${overviewTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setOverviewTab('dashboard')}
              >
                Dashboard
              </button>
              <button
                className={`vd-sub-tab ${overviewTab === 'events' ? 'active' : ''}`}
                onClick={() => setOverviewTab('events')}
              >
                Kommende Veranstaltungen
                {turniere.length > 0 && (
                  <span className="vd-sub-tab-count">{turniere.length}</span>
                )}
              </button>
            </div>

            {/* ── Dashboard ── */}
            {overviewTab === 'dashboard' && (
              <>
                <div className="vd-stats-row">
                  <div className="vd-stat-card vd-stat-card--blue">
                    <div className="vd-stat-value">{athletes.length}</div>
                    <div className="vd-stat-label">Wettkämpfer</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--green">
                    <div className="vd-stat-value">{turnierGroups.length}</div>
                    <div className="vd-stat-label">Turniere mit Starts</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--gold">
                    <div className="vd-stat-value">{safeAnmeldungen.length}</div>
                    <div className="vd-stat-label">Starts gesamt</div>
                  </div>
                  <div className="vd-stat-card vd-stat-card--red">
                    <div className="vd-stat-value">{activeStarts}</div>
                    <div className="vd-stat-label">Aktive Starts</div>
                  </div>
                </div>

                <div className="vd-row">
                  <div className="vd-card">
                    <div className="vd-card-header"><h3>Letzte Anmeldungen</h3></div>
                    {turnierGroups.length === 0 ? (
                      <p className="vd-empty">Noch keine Anmeldungen.</p>
                    ) : turnierGroups.slice(0, 5).map(t => (
                      <div key={t.id} className="vd-list-item">
                        <div>
                          <div className="vd-list-item-title">{t.name}</div>
                          <div className="vd-list-item-sub">{fmt(t.datum)} · {t.rows.length} Starts</div>
                        </div>
                        <span className="vd-badge vd-badge--success">
                          {t.rows.filter(r => r.status !== 'Storniert').length} aktiv
                        </span>
                      </div>
                    ))}
                    {turnierGroups.length > 5 && (
                      <button className="vd-link-btn" onClick={() => setTab('anmeldungen')}>
                        Alle {turnierGroups.length} Turniere anzeigen →
                      </button>
                    )}
                  </div>

                  <div className="vd-card">
                    <div className="vd-card-header"><h3>Nächste Veranstaltungen</h3></div>
                    {turniere.length === 0 ? (
                      <p className="vd-empty">Keine bevorstehenden Veranstaltungen.</p>
                    ) : turniere.slice(0, 4).map(t => (
                      <div key={t.turnier_id || t.id} className="vd-list-item">
                        <div>
                          <div className="vd-list-item-title">{t.name}</div>
                          <div className="vd-list-item-sub">{fmt(t.datum)} · {t.ort || '–'}</div>
                        </div>
                        {/* Dieselbe Ausschreibung wie öffentlich (22.10.2026) */}
                        <button
                          className="vd-btn vd-btn--gold vd-btn--sm"
                          onClick={() => navigate(`/event/${t.turnier_id || t.id}`)}
                        >
                          Ausschreibung
                        </button>
                      </div>
                    ))}
                    {turniere.length > 4 && (
                      <button className="vd-link-btn" onClick={() => setOverviewTab('events')}>
                        Alle {turniere.length} Veranstaltungen anzeigen →
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Kommende Veranstaltungen ── */}
            {overviewTab === 'events' && (
              <div>
                {turniere.length === 0 ? (
                  <div className="vd-empty-state">
                    <p>Aktuell sind keine Veranstaltungen ausgeschrieben.</p>
                  </div>
                ) : turniere.map(t => {
                  const tid = t.turnier_id || t.id;
                  const eigene = safeAnmeldungen.filter(a => a.turnier_id === tid && a.status !== 'Storniert').length;
                  const deadline = t.anmeldeschluss ? new Date(t.anmeldeschluss) : null;
                  // offen/zu sagt der Server (utils/anmeldeZustand.js, 22.10.2026):
                  // ohne Anmeldeschluss offen, am Turniertag bis zum Kategoriestart.
                  const deadlinePassed = t.anmeldung ? !t.anmeldung.anmeldbar : (deadline && deadline < new Date());
                  return (
                    <div key={tid} className="vd-card vd-event-card">
                      <div className="vd-turnier-header">
                        <div className="vd-event-info">
                          <div className="vd-turnier-name">{t.name}</div>
                          <div className="vd-turnier-meta">
                            <span>📅 {fmt(t.datum)}</span>
                            {t.ort && <span>📍 {t.ort}</span>}
                            {t.disziplin && <span>🥋 {t.disziplin}</span>}
                          </div>
                          {(deadline || t.anmeldung) && (
                            <div className={`vd-deadline ${deadlinePassed ? 'vd-deadline--passed' : ''}`}>
                              {t.anmeldung
                                ? `${t.anmeldung.text}${t.anmeldung.zusatz ? `, ${t.anmeldung.zusatz}` : ''}`
                                : `Anmeldeschluss: ${fmt(t.anmeldeschluss)}`}
                            </div>
                          )}
                          {t.beschreibung && (
                            <div className="vd-event-desc">{t.beschreibung}</div>
                          )}
                        </div>
                        <div className="vd-turnier-actions">
                          {eigene > 0 && (
                            <span className="vd-badge vd-badge--success">{eigene} angemeldet</span>
                          )}
                          {/* Dieselbe Ausschreibung wie öffentlich (22.10.2026) */}
                          <button className="vd-btn" onClick={() => navigate(`/event/${tid}`)}>Ausschreibung</button>
                          <button
                            className="vd-btn vd-btn--gold"
                            onClick={() => (eigene > 0 ? setTab('anmeldungen') : oeffneAnmeldung(t))}
                            disabled={deadlinePassed && eigene === 0}
                          >
                            {eigene > 0 ? 'Anmeldung ansehen' : deadlinePassed ? 'Abgelaufen' : 'Anmelden'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            WETTKÄMPFER
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'wettkämpfer' && (
          <div className="vd-card">
            <div className="vd-card-header">
              <h3>{athletes.length} Wettkämpfer</h3>
              <div className="vd-card-actions">
                <input
                  className="vd-search"
                  placeholder="Suchen…"
                  value={wkSearch}
                  onChange={e => setWkSearch(e.target.value)}
                />
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={() => wkOeffnen('add')}
                >
                  + Sportler anlegen
                </button>
              </div>
            </div>

            {/* ── Wer hat kein Gewicht? ────────────────────────────────────
                Seit dem 16.09.2026 ist das Gewicht beim Anlegen Pflicht. Das
                loest aber den BESTAND nicht: Wettkaempfer, die aus der
                Dojosoftware uebernommen wurden, kommen ohne Gewicht an (dort
                wird keines gefuehrt), und der Abgleich darf daran nicht
                scheitern -- sonst kommen gar keine Mitglieder mehr an.
                Gemessen am 16.09.: 81 von 81 uebernommenen ohne Gewicht,
                gegenueber 1 von 374 selbst angelegten.
                Deshalb steht die Luecke hier, statt still zu bleiben. */}
            {athletes.filter(a => !(parseFloat(a.gewicht) > 0)).length > 0 && (
              <div className="vd-gewicht-fehlt" role="status">
                <span aria-hidden="true">⚖️</span>
                <div>
                  <strong>
                    {athletes.filter(a => !(parseFloat(a.gewicht) > 0)).length} Wettkämpfer ohne Gewicht
                  </strong>
                  <div className="vd-gewicht-fehlt-zeile">
                    Für sie lässt sich keine Gewichtsklasse zuordnen — bei der Anmeldung stehen
                    dann alle Klassen zur Wahl, und bei der Einwaage muss die Gruppe neu gebaut
                    werden. In der Liste unten mit „⚖️ fehlt" markiert.
                  </div>
                </div>
              </div>
            )}

            {filteredAthletes.length === 0 ? (
              <p className="vd-empty">
                {wkSearch ? 'Keine Treffer.' : 'Noch keine Wettkämpfer erfasst.'}
              </p>
            ) : (
              <div className="vd-table-scroll">
              <table className="vd-table vd-table--wk vd-table--karten">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Geburtsdatum</th>
                    <th>Geschlecht</th>
                    <th>Gewicht</th>
                    <th>Gurtfarbe</th>
                    <th>Erfahrung</th>
                    <th>Starts</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAthletes.map(a => {
                    const wkId = a.wettkaempfer_id || a.id;
                    const starts = anmeldungen.filter(r =>
                      (r.wettkampfer_id || r.wettkaempfer_id) === wkId && r.status !== 'Storniert'
                    ).length;
                    return (
                      <tr key={wkId}>
                        <td className="vd-td-name vd-karte-titel">{a.nachname}, {a.vorname}</td>
                        <td data-label="Geburtsdatum">{fmt(a.geburtsdatum)}</td>
                        <td data-label="Geschlecht">{a.geschlecht || '–'}</td>
                        <td data-label="Gewicht">
                          {parseFloat(a.gewicht) > 0
                            ? `${parseFloat(a.gewicht).toLocaleString('de-DE', { maximumFractionDigits: 1 })} kg`
                            : <span className="vd-fehlt" title="Ohne Gewicht lässt sich keine Gewichtsklasse zuordnen.">⚖️ fehlt</span>}
                        </td>
                        <td data-label="Gurtfarbe">{a.gurtfarbe || '–'}</td>
                        <td data-label="Erfahrung">
                          {a.skill_level
                            ? <span className={`vd-badge vd-badge--${SKILL_BADGE[a.skill_level] || 'warning'}`}>{erfahrung(a.skill_level)}</span>
                            : '–'}
                        </td>
                        <td className="vd-td-gold" data-label="Starts">{starts}</td>
                        <td className="vd-karte-knoepfe">
                          <div className="vd-row-actions">
                            <button
                              className="vd-btn vd-btn--ghost vd-btn--sm"
                              onClick={() => wkOeffnen('edit', a)}
                            >
                              Bearbeiten
                            </button>
                            <button
                              className="vd-btn vd-btn--danger vd-btn--sm"
                              onClick={() => deleteWk(a)}
                            >
                              Löschen
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            VERANSTALTUNGEN
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'veranstaltungen' && (
          <div>
            {/* Kurzer Ablauf (27.09.2026) — vorher stand hier nichts dazu */}
            {turniere.length > 0 && (
              <p className="vd-hint" style={{ marginBottom: 12 }}>
                💡 So geht's: <strong>Anmelden</strong> → Wettkämpfer antippen → je Wettkämpfer die Kategorien
                wählen → fertig. Nachmelden geht jederzeit über denselben Knopf, bis zum Anmeldeschluss.
                Die Startgebühren siehst du danach unter <strong>Anmeldungen</strong>.
              </p>
            )}
            {turniere.length === 0 ? (
              <div className="vd-empty-state">
                <p>Aktuell sind keine Veranstaltungen ausgeschrieben.</p>
              </div>
            ) : turniere.map(t => {
              const tid = t.turnier_id || t.id;
              const eigene = anmeldungen.filter(a => a.turnier_id === tid && a.status !== 'Storniert').length;
              return (
                <div key={tid} className="vd-card">
                  <div className="vd-turnier-header">
                    <div>
                      <div className="vd-turnier-name">{t.name}</div>
                      <div className="vd-turnier-meta">
                        {/* ⚠️ Ein blosser Strich sagt nicht, was Sache ist.
                            „Termin noch offen" beantwortet die Frage, die der
                            Strich aufwirft — und erklaert, warum nur
                            vorgemerkt werden kann. */}
                        <span>📅 {t.datum ? fmt(t.datum) : 'Termin noch offen'}</span>
                        {t.ort && <span>📍 {t.ort}</span>}
                        {t.disziplin && <span>🥋 {t.disziplin}</span>}
                        {t.anmeldeschluss && <span>⏰ Anmeldeschluss: {fmt(t.anmeldeschluss)}</span>}
                        {t.anmeldung && <span>{t.anmeldung.text}{t.anmeldung.zusatz ? `, ${t.anmeldung.zusatz}` : ''}</span>}
                      </div>
                      {athletes.length === 0 && (
                        <p className="vd-hint">
                          Noch keine Sportler angelegt. „Anmelden“ führt dich zuerst zum Anlegen.
                        </p>
                      )}
                    </div>
                    <div className="vd-turnier-actions">
                      {eigene > 0 && (
                        <span className="vd-badge vd-badge--success">{eigene} angemeldet</span>
                      )}
                      <button className="vd-btn" onClick={() => navigate(`/event/${tid}`)}>Ausschreibung</button>
                      {/* Ohne Sportler nicht mehr blass und gesperrt (25.10.2026):
                          der Knopf führt zum Anlegen und merkt sich das Turnier. */}
                      <button
                        className="vd-btn vd-btn--haupt"
                        onClick={() => {
                          // Ohne Datum oder im Smart-Modus erst Sportler anlegen; die
                          // geführte Anmeldung macht das selbst in Schritt 1.
                          if (athletes.length === 0 && (!t.datum || t.modus === 'smart')) { setTurnierKontext({ ...t, turnier_id: tid }); setTab('wettkämpfer'); wkOeffnen('add'); return; }
                          oeffneAnmeldung(t);
                        }}
                        disabled={t.anmeldung && !t.anmeldung.anmeldbar}
                        title={t.anmeldung && !t.anmeldung.anmeldbar ? t.anmeldung.text : undefined}
                      >
                        {/* ⚠️ Ohne Datum ist das Turnier nur vormerkbar
                            (Entscheidung 16.09.2026). „Anmelden" waere das
                            falsche Wort -- es weckt die Erwartung, dass man
                            Kategorien waehlen kann. */}
                        {!t.datum ? (eigene > 0 ? 'Vormerkung bearbeiten' : 'Vormerken')
                          : (eigene > 0 ? 'Anmeldung ergänzen' : 'Anmelden')}
                      </button>
                      {t.datum && (
                        <button className="vd-btn" onClick={() => setCsvTurnier({ ...t, turnier_id: t.turnier_id || t.id })}
                          title="Viele Sportler auf einmal aus einer CSV-Datei anmelden">
                          Per Datei anmelden
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ANMELDUNGEN
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'anmeldungen' && (
          <div>
            {turnierGroups.length === 0 ? (
              <div className="vd-empty-state">
                <p>Noch keine Anmeldungen vorhanden.</p>
                <button className="vd-btn vd-btn--gold" onClick={() => setTab('veranstaltungen')}>
                  Zu Veranstaltungen
                </button>
              </div>
            ) : turnierGroups.map(t => (
              <div key={t.id} className="vd-card">
                <div className="vd-card-header">
                  <div>
                    <h3>{t.name}</h3>
                    <span className="vd-sub">{fmt(t.datum)}</span>
                  </div>
                  <span className="vd-badge vd-badge--gold">{t.rows.length} Starts</span>
                </div>
                {/* ⚠️ Die Liste zeigte keinen einzigen Betrag. Der Verein
                    konnte nicht sehen, was eine Anmeldung kostet -- und
                    schon gar nicht, dass die zweite und jede weitere
                    Kategorie guenstiger ist. Gezeigt wird die TATSAECHLICHE
                    Gebuehr der Anmeldung, nicht der Turnierpreis. */}
                <div className="vd-table-scroll">
                <table className="vd-table vd-table--anm vd-table--karten">
                  <thead>
                    <tr>
                      <th>Wettkämpfer</th>
                      <th>Kategorie</th>
                      {/* Matte und Uhrzeit, sobald der Zeitplan steht (27.09.2026) */}
                      {t.rows.some(r => r.matte_nummer || r.uhrzeit) && <th>Matte · Uhrzeit</th>}
                      <th>Angemeldet am</th>
                      <th>Status</th>
                      <th className="vd-td-geld">Startgebühr</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="vd-td-name vd-karte-titel">{r.nachname}, {r.vorname}</td>
                        <td data-label="Kategorie">
                          {r.division_code && r.division_name
                            ? <>{deutscherName({ Division_Name: r.division_name, Division_Type: r.division_type, Division_Code: r.division_code })} <span className="vd-code">{r.division_code}</span></>
                            : (r.division_code || r.kategorie || 'Vorgemerkt, Kategorie fehlt')}
                          {r.team_name && <div className="vd-hint">Team: {r.team_name}</div>}
                          {/* Wunsch aus der Selbstanmeldung des Athleten */}
                          {!r.division_code && r.notizen && <div className="vd-hint">{r.notizen}</div>}
                        </td>
                        {t.rows.some(x => x.matte_nummer || x.uhrzeit) && (
                          <td data-label="Matte · Uhrzeit">
                            {r.matte_nummer ? (r.matte_name || `Matte ${r.matte_nummer}`) : '–'}
                            {r.uhrzeit && <div className="vd-hint">ca. {r.uhrzeit} Uhr</div>}
                          </td>
                        )}
                        <td data-label="Angemeldet am">{fmt(r.anmeldedatum || r.erstellt_am)}</td>
                        <td data-label="Status">
                          <span className={`vd-badge vd-badge--${statusBadge(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="vd-td-geld" data-label="Startgebühr">
                          {euro(r.startgebuehr)}
                          {r.bezahlt ? <span className="vd-bezahlt" title="bezahlt"> ✓</span> : null}
                          {/* Teamgebühr je Team: bei „eine Position für den Verein" steht sie beim ersten Mitglied */}
                          {r.team_name && parseFloat(r.startgebuehr) === 0 && r.status !== 'Storniert' && (
                            <div className="vd-hint">im Teampreis enthalten</div>
                          )}
                        </td>
                        <td className="vd-karte-knoepfe">
                          {r.status !== 'Storniert' && (
                            <button
                              className="vd-btn vd-btn--danger vd-btn--sm"
                              onClick={() => storniereAnmeldung(r)}
                            >
                              Stornieren
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="vd-summe-label">
                        Summe der offenen Starts
                      </td>
                      <td className="vd-td-geld vd-summe" data-label="Summe der offenen Starts">{euro(summeOffen(t.rows))}</td>
                      <td className="vd-karte-leer" />
                    </tr>
                  </tfoot>
                </table>
                </div>
                {/* Aufgabe 141: offene Gebühr per GiroCode bezahlen */}
                {summeOffen(t.rows) > 0 && vereinsId && <ZahlungKasten turnierId={t.id} vereinsId={vereinsId} headers={headers} />}
              </div>
            ))}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PROFIL
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'profil' && (
          <div className="vd-card">
            <div className="vd-card-header">
              <h3>Vereinsprofil</h3>
              {!editProfile ? (
                <button className="vd-btn vd-btn--gold" onClick={() => setEditProfile(true)}>
                  Bearbeiten
                </button>
              ) : (
                <div className="vd-card-actions">
                  <button className="vd-btn vd-btn--ghost" onClick={() => setEditProfile(false)}>
                    Abbrechen
                  </button>
                  <button className="vd-btn vd-btn--success" onClick={saveProfile}>
                    Speichern
                  </button>
                </div>
              )}
            </div>
            <div className="vd-profile-grid">
              {[
                ['name',            'Vereinsname'],
                ['email',           'E-Mail'],
                ['ansprechpartner', 'Ansprechpartner'],
                ['telefon',         'Telefon'],
                ['homepage',        'Homepage'],
                ['strasse',         'Straße'],
                ['hausnummer',      'Hausnummer'],
                ['plz',             'PLZ'],
                ['ort',             'Ort'],
              ].map(([field, label]) => (
                <div key={field} className="vd-profile-field">
                  <label className="vd-label">{label}</label>
                  {editProfile ? (
                    <input
                      className="vd-input"
                      value={profileForm[field] || ''}
                      onChange={e => setProfileForm({ ...profileForm, [field]: e.target.value })}
                    />
                  ) : (
                    <div className="vd-profile-value">{profile?.[field] || '–'}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            INTEGRATION — Dojosoftware API
        ══════════════════════════════════════════════════════════════════ */}
        {tab === 'integration' && (
          <div>
            {/* Status-Card */}
            <div className={`vd-card vd-integ-status ${integration ? 'vd-integ-status--connected' : 'vd-integ-status--none'}`}>
              <div className="vd-integ-status-icon">{integration ? '🔗' : '⚡'}</div>
              <div>
                <div className="vd-integ-status-title">
                  {integration ? 'Dojosoftware verbunden' : 'Noch nicht konfiguriert'}
                </div>
                {integration?.last_sync ? (
                  <div className="vd-integ-status-sub">
                    Letzter Sync: {fmt(integration.last_sync)}{' '}
                    {new Date(integration.last_sync).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                  </div>
                ) : integration ? (
                  <div className="vd-integ-status-sub">Noch kein Sync durchgeführt.</div>
                ) : (
                  <div className="vd-integ-status-sub">
                    Verbinden Sie Ihre Dojosoftware-Instanz, um Wettkämpfer automatisch zu importieren.
                  </div>
                )}
              </div>
              {integration && (
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={syncNow}
                  disabled={integLoading}
                  style={{ marginLeft: 'auto' }}
                >
                  {integLoading ? 'Synchronisiert…' : '↻ Jetzt synchronisieren'}
                </button>
              )}
            </div>

            {/* Konfigurations-Form */}
            <div className="vd-card">
              <div className="vd-card-header">
                <h3>API-Konfiguration</h3>
              </div>
              <div className="vd-profile-grid">
                <div className="vd-form-field vd-form-field--full">
                  <label className="vd-label">Dojosoftware API-URL</label>
                  <input
                    className="vd-input"
                    placeholder="https://dojo.example.org/api"
                    value={integForm.dojo_api_url}
                    onChange={e => setIntegForm(f => ({ ...f, dojo_api_url: e.target.value }))}
                  />
                  <span className="vd-hint">
                    Basis-URL der Dojosoftware-API, z.B. <code>https://dojo.tda-intl.org/api</code>
                  </span>
                </div>
                <div className="vd-form-field vd-form-field--full">
                  <label className="vd-label">
                    API-Token{integration?.has_key ? ' (gespeichert — neuen Token eingeben um zu ändern)' : ''}
                  </label>
                  <input
                    className="vd-input"
                    type="password"
                    placeholder={integration?.has_key ? '••••••••••••••••' : 'API-Token eingeben'}
                    value={integForm.api_token}
                    onChange={e => setIntegForm(f => ({ ...f, api_token: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <span className="vd-hint">
                    Den API-Token finden Sie in Ihren Dojosoftware-Einstellungen unter Integrationen.
                  </span>
                </div>
              </div>

              {/* Test-Ergebnis */}
              {integTestResult && (
                <div className={`vd-message vd-message--${integTestResult.ok ? 'success' : 'error'}`} style={{ margin: '0 0 16px' }}>
                  {integTestResult.msg}
                </div>
              )}

              <div className="vd-integ-actions">
                <button
                  className="vd-btn vd-btn--ghost"
                  onClick={testConnection}
                  disabled={integLoading || !integForm.dojo_api_url || !integForm.api_token}
                >
                  {integLoading ? 'Teste…' : '🔍 Verbindung testen'}
                </button>
                <button
                  className="vd-btn vd-btn--gold"
                  onClick={saveIntegration}
                  disabled={integLoading || !integForm.dojo_api_url || !integForm.api_token}
                >
                  {integLoading ? 'Speichert…' : '💾 Speichern'}
                </button>
              </div>
            </div>

            {/* Info-Box */}
            <div className="vd-card vd-integ-info">
              <h4>So funktioniert die Integration</h4>
              <ol>
                <li>Geben Sie die API-URL Ihrer Dojosoftware-Instanz ein.</li>
                <li>Tragen Sie den API-Token ein (zu finden in Dojosoftware → Einstellungen → Integrationen).</li>
                <li>Testen Sie die Verbindung und speichern Sie die Konfiguration.</li>
                <li>Klicken Sie auf <strong>Jetzt synchronisieren</strong> — Ihre Wettkämpfer werden automatisch importiert.</li>
              </ol>
              <p className="vd-hint" style={{ marginTop: 8 }}>
                Beim Sync werden bestehende Wettkämpfer (erkannt über die Dojo-ID) aktualisiert, neue werden hinzugefügt.
              </p>
            </div>
          </div>
        )}

        </>}
      </div>{/* /vd-content */}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Wettkämpfer hinzufügen / bearbeiten
      ══════════════════════════════════════════════════════════════════════ */}
      {wkModal && (
        // Klick neben das Fenster schließt NICHT mehr (25.10.2026): vorher waren
        // die Eingaben mit einem Fehlklick weg.
        <div className="vd-modal-overlay">
          <div className="vd-modal vd-modal--wk" role="dialog" aria-modal="true" aria-labelledby="wk_titel">
            <div className="vd-modal-header">
              <h3 id="wk_titel">{wkModal.type === 'add' ? 'Sportler anlegen' : 'Sportler bearbeiten'}</h3>
              <button className="vd-modal-close" onClick={wkSchliessen} aria-label="Fenster schließen">✕</button>
            </div>
            <div className="vd-modal-body">
              {wkMeldung && <div className="vd-wk-meldung" role="alert">{wkMeldung}</div>}
              <div className="vd-form-grid">
                {[['vorname', 'Vorname', 'given-name'], ['nachname', 'Nachname', 'family-name']].map(([f, l, ac]) => (
                  <div key={f} className={`vd-form-field ${wkFehler[f] ? 'vd-feld-fehler' : ''}`}>
                    <label className="vd-label" htmlFor={`wk_${f}`}>{l} <span className="vd-pflicht" aria-hidden="true">*</span></label>
                    <input
                      id={`wk_${f}`}
                      className="vd-input"
                      autoComplete={ac}
                      value={wkForm[f] || ''}
                      aria-invalid={wkFehler[f] ? 'true' : undefined}
                      onChange={e => wkSetze(f, e.target.value)}
                    />
                    {wkFehler[f] && <small className="vd-feldfehler" role="alert">{wkFehler[f]}</small>}
                  </div>
                ))}
                {/* Das Geburtsdatum ist serverseitig seit jeher Pflicht (5-99 Jahre) */}
                <div className={`vd-form-field ${wkFehler.geburtsdatum ? 'vd-feld-fehler' : ''}`}>
                  <label className="vd-label" htmlFor="wk_geburtsdatum">
                    Geburtsdatum <span className="vd-pflicht" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="wk_geburtsdatum"
                    type="date" required
                    className="vd-input"
                    value={wkForm.geburtsdatum ? String(wkForm.geburtsdatum).split('T')[0] : ''}
                    aria-invalid={wkFehler.geburtsdatum ? 'true' : undefined}
                    onChange={e => wkSetze('geburtsdatum', e.target.value)}
                  />
                  {wkFehler.geburtsdatum
                    ? <small className="vd-feldfehler" role="alert">{wkFehler.geburtsdatum}</small>
                    : <small className="vd-feldhinweis">Bestimmt die Altersklasse. Zwischen 5 und 99 Jahren.</small>}
                </div>
                {/* Geschlecht ohne Vorbelegung (25.10.2026), Pflicht wie am Server */}
                <div className={`vd-form-field ${wkFehler.geschlecht ? 'vd-feld-fehler' : ''}`} role="group" aria-labelledby="wk_geschlecht_l">
                  <span className="vd-label" id="wk_geschlecht_l">Startet bei <span className="vd-pflicht" aria-hidden="true">*</span></span>
                  <div className="vd-wahl">
                    {[['weiblich', 'Mädchen / Frauen'], ['männlich', 'Jungen / Männer']].map(([w, l], i) => (
                      <button key={w} type="button" id={i === 0 ? 'wk_geschlecht' : undefined}
                        className={`vd-wahl-knopf ${wkForm.geschlecht === w ? 'an' : ''}`}
                        aria-pressed={wkForm.geschlecht === w}
                        onClick={() => wkSetze('geschlecht', w)}>{l}</button>
                    ))}
                  </div>
                  {wkFehler.geschlecht && <small className="vd-feldfehler" role="alert">{wkFehler.geschlecht}</small>}
                </div>
                {/* ⚠️ Pflichtfeld seit dem 16.09.2026: ohne Gewicht keine Gewichtsklasse */}
                <div className={`vd-form-field ${wkFehler.gewicht ? 'vd-feld-fehler' : ''}`}>
                  <label className="vd-label" htmlFor="wk_gewicht">
                    Gewicht (kg) <span className="vd-pflicht" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="wk_gewicht"
                    type="number" step="0.1" min="15" max="250" required inputMode="decimal"
                    className="vd-input"
                    value={wkForm.gewicht ?? ''}
                    aria-invalid={wkFehler.gewicht ? 'true' : undefined}
                    onChange={e => wkSetze('gewicht', e.target.value)}
                    placeholder="z. B. 38"
                  />
                  {wkFehler.gewicht
                    ? <small className="vd-feldfehler" role="alert">{wkFehler.gewicht}</small>
                    : <small className="vd-feldhinweis">Ohne Gewicht lässt sich keine Gewichtsklasse zuordnen.</small>}
                </div>
                <div className="vd-form-field">
                  <label className="vd-label" htmlFor="wk_gurtfarbe">Gurtfarbe</label>
                  <select
                    id="wk_gurtfarbe"
                    className="vd-input"
                    value={wkForm.gurtfarbe || 'Weiß'}
                    onChange={e => wkSetze('gurtfarbe', e.target.value, { skill_level: gurtToSkill(e.target.value) })}
                  >
                    {GURTFARBEN.map(g => <option key={g}>{g}</option>)}
                  </select>
                  <small className="vd-feldhinweis">Die Farbe, die der Sportler jetzt trägt.</small>
                </div>
                {/* „Skill Level“ → Erfahrung (25.10.2026). Vorschlag aus dem Gurt, änderbar */}
                <div className="vd-form-field">
                  <label className="vd-label" htmlFor="wk_skill_level">Erfahrung</label>
                  <select
                    id="wk_skill_level"
                    className="vd-input"
                    value={wkForm.skill_level || gurtToSkill(wkForm.gurtfarbe)}
                    onChange={e => wkSetze('skill_level', e.target.value)}
                  >
                    {SKILL_LEVELS.map(s => <option key={s} value={s}>{erfahrung(s)}</option>)}
                  </select>
                  <small className="vd-feldhinweis">
                    Ergibt sich aus dem Gurt (Weiß bis Orange: Anfänger, Grün und Blau: Fortgeschritten,
                    Rot und Braun: Erfahren, Schwarz: Schwarzgurt). Danach richten sich die Startklassen.
                  </small>
                </div>
                <div className="vd-form-field">
                  <label className="vd-label" htmlFor="wk_email">E-Mail <span className="vd-frei">(freiwillig)</span></label>
                  <input
                    id="wk_email"
                    type="email"
                    className="vd-input"
                    value={wkForm.email || ''}
                    aria-invalid={wkFehler.email ? 'true' : undefined}
                    onChange={e => wkSetze('email', e.target.value)}
                  />
                  {wkFehler.email && <small className="vd-feldfehler" role="alert">{wkFehler.email}</small>}
                </div>
                {/* Foto-Einwilligung beim Sportler (25.10.2026). Gilt als Vorgabe für
                    jede Turnieranmeldung, für die noch nichts entschieden ist
                    (services/fotogalerie.js, vorgabenUebernehmen). */}
                <div className="vd-form-field vd-form-field--full" role="group" aria-labelledby="wk_foto_l">
                  <span className="vd-label" id="wk_foto_l">Dürfen Fotos vom Turnier in die öffentliche Bildergalerie?</span>
                  <div className="vd-wahl">
                    {[[1, 'Ja, einverstanden'], [0, 'Nein']].map(([w, l], i) => (
                      <button key={w} type="button" id={i === 0 ? 'wk_foto_galerie_vorgabe' : undefined}
                        className={`vd-wahl-knopf ${wkForm.foto_galerie_vorgabe !== null && wkForm.foto_galerie_vorgabe !== undefined && Number(wkForm.foto_galerie_vorgabe) === w ? 'an' : ''}`}
                        aria-pressed={wkForm.foto_galerie_vorgabe !== null && wkForm.foto_galerie_vorgabe !== undefined && Number(wkForm.foto_galerie_vorgabe) === w}
                        onClick={() => wkSetze('foto_galerie_vorgabe', w)}>{l}</button>
                    ))}
                  </div>
                  <details className="vd-hilfe">
                    <summary>Was heißt das?</summary>
                    <p>
                      Auf dem Turnier wird fotografiert. Mit Ja dürfen Fotos, auf denen der Sportler gut zu erkennen ist,
                      in der öffentlichen Bildergalerie des Turniers erscheinen. Bei Kindern und Jugendlichen unter 18
                      bestätigst du damit, dass die Eltern zugestimmt haben. Ohne Ja erscheint kein Foto in der Galerie;
                      mitmachen darf der Sportler trotzdem. Die Antwort lässt sich jederzeit ändern, auch je Turnier im
                      Anmeldefenster.
                    </p>
                  </details>
                </div>
              </div>
            </div>
            <div className="vd-modal-footer">
              <button className="vd-btn vd-btn--ghost" onClick={wkSchliessen}>
                Abbrechen
              </button>
              <button
                className="vd-btn vd-btn--haupt"
                onClick={saveWk}
                disabled={wkSpeichert}
              >
                {wkSpeichert ? 'Wird gespeichert…' : wkModal.type === 'add' ? 'Sportler speichern' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL: Turnier-Anmeldung
      ══════════════════════════════════════════════════════════════════════ */}
      {turnierModal && (
        <div className="vd-modal-overlay" onClick={() => setTurnierModal(null)}>
          <div className="vd-modal vd-modal--large" onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>Anmeldung: {turnierModal.turnier.name}</h3>
              <button className="vd-modal-close" onClick={() => setTurnierModal(null)}>✕</button>
            </div>
            <div className="vd-modal-body">
              <p className="vd-hint" style={{ marginBottom: 16 }}>
                {fmt(turnierModal.turnier.datum)}
                {turnierModal.turnier.ort ? ` · ${turnierModal.turnier.ort}` : ''}
              </p>
              <label className="vd-label" style={{ marginBottom: 8, display: 'block' }}>
                Wettkämpfer auswählen:
              </label>
              <div className="vd-athlete-list">
                {athletes.map(a => {
                  const id = a.wettkaempfer_id || a.id;
                  const checked = selectedAthletes.has(id);
                  return (
                    <label key={id} className={`vd-athlete-item ${checked ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAthlete(id)}
                      />
                      <span className="vd-athlete-name">{a.nachname}, {a.vorname}</span>
                      <span className="vd-athlete-meta">
                        {a.gurtfarbe || '–'} · {a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : '–'}
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedAthletes.size > 0 && (
                <div className="vd-modal-summary">
                  {selectedAthletes.size} Wettkämpfer ausgewählt
                </div>
              )}
              {/* Nur noch für Turniere OHNE Datum — mit Datum öffnet „Anmelden"
                  direkt die Kategorien-Anmeldung (oeffneAnmeldung). */}
              <div className="vd-modal-hinweis">
                Für dieses Turnier steht <strong>noch kein Datum</strong> fest. Es lässt
                sich deshalb nur <strong>vormerken</strong> — Kategorien werden erst
                gewählt, wenn der Termin steht. Die Vormerkung bleibt bestehen und
                lässt sich dann über „Anmelden" ergänzen.
              </div>
            </div>
            <div className="vd-modal-footer">
              <button className="vd-btn vd-btn--ghost" onClick={() => setTurnierModal(null)}>
                Abbrechen
              </button>
              <button
                className="vd-btn vd-btn--gold"
                onClick={submitTurnierAnmeldung}
                disabled={selectedAthletes.size === 0}
              >
                {selectedAthletes.size > 0
                  ? `${selectedAthletes.size} vormerken`
                  : 'Vormerken'}
              </button>
            </div>
          </div>
        </div>
      )}

      {anmeldeTurnier && (
        <WettkampferAuswahlModal
          turnier={anmeldeTurnier}
          onClose={() => setAnmeldeTurnier(null)}
          onAnmeldungAbgeschlossen={anmeldungGespeichert}
        />
      )}

      {csvTurnier && (
        <CsvAnmeldungModal
          turnier={csvTurnier}
          vereinId={vereinsId}
          token={token}
          onClose={() => setCsvTurnier(null)}
          onFertig={() => loadAll(true)}
        />
      )}

    </div>
  );
}
